import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Bill, BillDeduction, BillTransaction } from "@customTypes/billing";

import { billingQKeys } from "./queryKeys";


const select = (res: any) => {
  // console.log(res.data);

  if (Object.keys(res.data).length) {
    const newData = {} as Bill;
    const {
      _id,
      created_at,
      updated_at,
      total,
      total_accepted,
      balance,
      deductions,
      subtotal,
      amount_paid,
      initial_deduction_taken,
      total_after_deduction,
      consultation_reason,
      services,
      transactions,
      status,
      patient_id,
      consultation_id,
      consultation_info,
      date,
      files,
    } = res.data;

    newData.id = _id;
    newData.total = total ?? 0;
    newData.balance = balance ?? 0;
    newData.totalAccepted = total_accepted ?? 0;
    newData.deductions = deductions?.length
      ? deductions.map(
          (deduction: any) =>
            ({
              discountType: deduction.discount_type,
              discountAmount: deduction.discount_amount,
              discountPercentage: deduction.discount_percentage,
              createdBy: deduction.created_by,
              createdAt: new Date(deduction.created_at),
              extraInfo: deduction.extra_info,
            } satisfies BillDeduction)
        )
      : [];
    newData.subtotal = subtotal;
    newData.amountPaid = amount_paid;
    newData.initialDeductionTaken = initial_deduction_taken;
    newData.totalAfterDeduction = total_after_deduction ?? 0;
    newData.consultationReason = consultation_reason;
    newData.services = services?.length
      ? services.map((item: any) => ({
          id: item.service_id,
          category: item.category,
          supportStaff: {
            id: item.aid?._id,
            fullName: item.aid?.full_name,
          },
          discount: item.discount,
          price: item.price,
          type: item.type,
          finalized: item.finalized,
          quantity: item.quantity,
          status: item.status,
          createdAt: item.created_at
            ? new Date(item.created_at)
            : new Date(1900, 0, 1),
        }))
      : [];
    newData.transactions = transactions?.length
      ? transactions
          .map((transaction: any) => {
            return {
              date: new Date(transaction.date),
              paymentMethod: transaction.payment_method,
              oldBalance: transaction.old_balance ?? 0,
              newBalance: transaction.new_balance ?? 0,
              amountPaid: transaction.amount_paid ?? 0,
              receivedBy: transaction.received_by,
              discount: transaction.discount,
              promoCode: transaction.promo_code,
            };
          })
          .sort(
            (a: BillTransaction, b: BillTransaction) =>
              b.date.getTime() - a.date.getTime()
          )
      : [];
    newData.status = status;
    newData.date = new Date(date);
    newData.patientId = patient_id;
    newData.consultationId = consultation_id;
    newData.consultationInfo = {
      createdAt: new Date(consultation_info?.created_at),
      updatedAt: new Date(consultation_info.updated_at),
      date: new Date(consultation_info.date),
      supportStaff: consultation_info.aid?.map((item: any) => ({
        id: item._id,
        fullName: item.full_name,
      })),
      physician: {
        id: consultation_info.physician.id,
        fullName: consultation_info.physician.full_name,
      },
    };

    newData.files = files;
    newData.createdAt = new Date(created_at);
    newData.updatedAt = new Date(updated_at);

    // console.log(newData);
    return newData;
  }
  return null;
};

interface QueryProps {
  consultationId: string;
  isEnabled?: boolean;
}

export const useGetBillForConsultation = ({
  consultationId,
  isEnabled = true,
}: QueryProps) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: billingQKeys.consultation(consultationId),
    queryFn: () => {
      return api.get(`billing/consultation/${consultationId}`);
    },
    select,
    enabled: isEnabled,

    onError: (res: any) => {
      console.error("[From Get Bill for Consultation]", res);
      handleErrors(res, "query");
    },
  });
};
